import React, { useEffect, useState, Children } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Package, Mail, LogOut, ArrowRight, CheckCircle2, Clock, Truck, Download, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeToOrders, type Order } from '../../../utils/firebase';
import { useCart } from '../../../contexts/CartContext';
import { getBookDownloadUrl } from '../../../utils/downloads';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function DownloadButton({
  orderId
}: {
  orderId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        url
      } = await getBookDownloadUrl(orderId);
      // Open the signed URL in a new tab so the user keeps their account page.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message ?? 'Download failed. Try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="flex flex-col items-start gap-2" data-id="element-1329">
      <button type="button" onClick={handleClick} disabled={loading} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2" data-id="element-1330">
        {loading ? <>
            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1331" />
            Preparing…
          </> : <>
            <Download className="w-3.5 h-3.5" data-id="element-1332" />
            Download
          </>}
      </button>
      {error && <p className="text-[10px] text-burgundy leading-relaxed max-w-xs" data-id="element-1333">
          {error}
        </p>}
    </div>;
}
function FulfillmentBadge({
  order
}: {
  order: Order;
}) {
  const config = (() => {
    switch (order.fulfillmentStatus) {
      case 'digital_delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'shipped':
        return {
          icon: Truck,
          label: 'Shipped',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'pending':
      default:
        return {
          icon: Clock,
          label: 'Processing',
          color: 'text-burgundy',
          bg: 'bg-burgundy/10'
        };
    }
  })();
  const Icon = config.icon;
  return <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${config.bg} ${config.color} text-[10px] font-bold uppercase tracking-[0.2em]`} data-id="element-1334">
      <Icon className="w-3 h-3" data-id="element-1335" />
      {config.label}
    </span>;
}
export function Account() {
  const {
    user,
    profile,
    loading: authLoading,
    signOut,
    resendVerification
  } = useAuth();
  const navigate = useNavigate();
  const {
    clearCart
  } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderSuccess = searchParams.get('order') === 'success';
  const [showSuccess, setShowSuccess] = useState(orderSuccess);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);
  const handleResendVerification = async () => {
    setResendState('sending');
    setResendError(null);
    try {
      await resendVerification();
      setResendState('sent');
      setTimeout(() => setResendState('idle'), 6000);
    } catch (err: any) {
      setResendState('error');
      setResendError(err?.message ?? 'Could not send verification email. Try again.');
    }
  };
  useEffect(() => {
    if (orderSuccess) {
      clearCart();
      const next = new URLSearchParams(searchParams);
      next.delete('order');
      setSearchParams(next, {
        replace: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSuccess]);
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', {
        state: {
          from: '/account'
        },
        replace: true
      });
    }
  }, [user, authLoading, navigate]);
  // Subscribe to orders
  useEffect(() => {
    if (!user) return;
    setOrdersLoading(true);
    setOrdersError(null);
    const unsub = subscribeToOrders(user.uid, next => {
      setOrders(next);
      setOrdersLoading(false);
    });
    return unsub;
  }, [user]);
  const handleSignOut = async () => {
    await signOut();
    navigate('/', {
      replace: true
    });
  };
  if (authLoading || !user) {
    return <div className="w-full min-h-screen bg-bodybg flex items-center justify-center pt-32 pb-20" data-id="element-1336">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1337" />
      </div>;
  }
  const displayName = profile?.displayName ?? user.displayName ?? 'there';
  const firstName = displayName.split(' ')[0];
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1338">
      <AnimatePresence data-id="element-1339">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed top-24 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl" data-id="element-1340">
            <div className="bg-tealslate text-white shadow-2xl border-l-4 border-lightteal p-5 flex items-start gap-4" data-id="element-1341">
              <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" data-id="element-1342" />
              <div className="flex-1" data-id="element-1343">
                <p className="font-serif text-lg mb-1" data-id="element-1344">Order received.</p>
                <p className="text-sm text-white/90 leading-relaxed" data-id="element-1345">
                  Thanks for your purchase — your order will appear below in a
                  moment. A receipt has been emailed to you.
                </p>
              </div>
              <button onClick={() => setShowSuccess(false)} className="text-white/70 hover:text-white transition-colors" aria-label="Dismiss" data-id="element-1346">
                <X className="w-4 h-4" data-id="element-1347" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* HERO */}
      <section className="bg-bodybg pt-40 pb-12 px-6 border-b border-darkgray" data-id="element-1348">
        <div className="max-w-5xl mx-auto" data-id="element-1349">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1350">
            <motion.p variants={fadeUp} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1351">
              Your Account
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1352">
              Hi, {firstName}.
            </motion.h1>
            <motion.div variants={fadeUp} className="w-16 h-0.5 bg-tealslate" data-id="element-1353" />
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="bg-[#F5F5F0] py-16 md:py-24 px-6" data-id="element-1354">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1355">
          {/* SIDEBAR */}
          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="space-y-6 lg:col-span-1" data-id="element-1356">
            <div className="bg-charcoal text-white p-8 border-l-4 border-tealslate" data-id="element-1357">
              <UserIcon className="w-6 h-6 text-tealslate mb-4" data-id="element-1358" />
              <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-3" data-id="element-1359">
                Profile
              </p>
              <p className="text-lg font-serif text-white mb-1" data-id="element-1360">
                {displayName}
              </p>
              <p className="text-sm text-[#D8D8D0] flex items-center gap-2" data-id="element-1361">
                <Mail className="w-3.5 h-3.5" data-id="element-1362" />
                {user.email}
              </p>
              {!user.emailVerified && <div className="mt-4 pt-4 border-t border-white/10" data-id="element-1363">
                  <div className="flex items-start gap-2 mb-3" data-id="element-1364">
                    <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1365" />
                    <p className="text-xs text-[#D8D8D0] leading-relaxed" data-id="element-1366">
                      Email not verified. Check your inbox for a verification
                      link.
                    </p>
                  </div>
                  <button onClick={handleResendVerification} disabled={resendState === 'sending' || resendState === 'sent'} className="w-full text-[10px] font-bold uppercase tracking-[0.2em] py-2.5 px-3 border border-tealslate/40 text-tealslate hover:bg-tealslate hover:text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-id="element-1367">
                    {resendState === 'sending' && <>
                        <span className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" data-id="element-1368" />
                        Sending…
                      </>}
                    {resendState === 'sent' && <>
                        <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1369" />
                        Verification email sent
                      </>}
                    {(resendState === 'idle' || resendState === 'error') && <>
                        <Mail className="w-3.5 h-3.5" data-id="element-1370" />
                        Resend verification email
                      </>}
                  </button>
                  {resendState === 'error' && resendError && <p className="text-[10px] text-burgundy mt-2 leading-relaxed" data-id="element-1371">
                      {resendError}
                    </p>}
                  {resendState === 'sent' && <p className="text-[10px] text-[#9A9A90] mt-2 leading-relaxed" data-id="element-1372">
                      Check your inbox (and spam folder). The link expires in 24
                      hours.
                    </p>}
                </div>}
            </div>

            <button onClick={handleSignOut} className="w-full bg-white border border-[#D8D8D0] p-6 hover:border-tealslate hover:bg-tealslate/5 transition-all group flex items-center justify-between" data-id="element-1373">
              <span className="text-xs font-bold text-[#1F1F1F] uppercase tracking-[0.2em] group-hover:text-tealslate transition-colors" data-id="element-1374">
                Sign Out
              </span>
              <LogOut className="w-4 h-4 text-[#9A9A90] group-hover:text-tealslate transition-colors" data-id="element-1375" />
            </button>
          </motion.aside>

          {/* ORDERS */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-2" data-id="element-1376">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-3" data-id="element-1377">
              Order History
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-10" data-id="element-1378">
              Your orders.
            </h2>

            {ordersLoading ? <div className="bg-white border border-[#D8D8D0] p-12 flex items-center justify-center" data-id="element-1379">
                <span className="w-5 h-5 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1380" />
              </div> : ordersError ? <div className="bg-burgundy/10 border-l-4 border-burgundy p-6 flex gap-3 items-start" data-id="element-1381">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1382" />
                <p className="text-sm text-burgundy" data-id="element-1383">{ordersError}</p>
              </div> : orders.length === 0 ? <div className="bg-white border border-[#D8D8D0] p-12 text-center" data-id="element-1384">
                <Package className="w-10 h-10 text-[#9A9A90] mx-auto mb-4" data-id="element-1385" />
                <h3 className="text-xl font-serif text-[#1F1F1F] mb-3" data-id="element-1386">
                  No orders yet.
                </h3>
                <p className="text-sm text-[#3B3B3B] leading-relaxed mb-6 max-w-sm mx-auto" data-id="element-1387">
                  When you purchase a book or supplement, your order will appear
                  here with tracking and download links.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1388">
                  <Link to="/books" className="px-6 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2" data-id="element-1389">
                    Browse Books <ArrowRight className="w-3 h-3" data-id="element-1390" />
                  </Link>
                  <Link to="/shop" className="px-6 py-3 bg-white border border-[#1F1F1F] text-[#1F1F1F] text-xs font-bold uppercase tracking-wider hover:bg-[#1F1F1F] hover:text-white transition-colors inline-flex items-center justify-center gap-2" data-id="element-1391">
                    Shop Supplements <ArrowRight className="w-3 h-3" data-id="element-1392" />
                  </Link>
                </div>
              </div> : <ul className="space-y-6" data-id="element-1393">
                {orders.map(order => <li key={order.id} className="bg-white border border-[#D8D8D0] p-6 md:p-8" data-id="element-1394">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-6 pb-6 border-b border-[#D8D8D0]" data-id="element-1395">
                      <div data-id="element-1396">
                        <p className="text-[10px] font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-2" data-id="element-1397">
                          Order #{order.id.slice(0, 8).toUpperCase()}
                        </p>
                        <p className="text-sm text-[#3B3B3B]" data-id="element-1398">
                          {formatDate(order.createdAt)}
                        </p>
                      </div>
                      <FulfillmentBadge order={order} data-id="element-1399" />
                    </div>

                    <ul className="space-y-3 mb-6" data-id="element-1400">
                      {order.lineItems.map((item, idx) => <li key={idx} className="flex items-start justify-between gap-4 text-sm" data-id="element-1401">
                          <div data-id="element-1402">
                            <p className="font-bold text-[#1F1F1F]" data-id="element-1403">
                              {item.name}
                            </p>
                            <p className="text-xs text-[#7A7A70] mt-1" data-id="element-1404">
                              Qty {item.quantity} ·{' '}
                              {item.type === 'book' ? 'Digital' : 'Physical'}
                            </p>
                          </div>
                          <p className="text-[#1F1F1F] font-bold whitespace-nowrap" data-id="element-1405">
                            {formatPrice(item.unitPrice * item.quantity)}
                          </p>
                        </li>)}
                    </ul>

                    <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-[#D8D8D0]" data-id="element-1406">
                      <div data-id="element-1407">
                        <p className="text-[10px] font-bold text-[#9A9A90] uppercase tracking-[0.2em] mb-1" data-id="element-1408">
                          Total
                        </p>
                        <p className="text-xl font-serif text-[#1F1F1F]" data-id="element-1409">
                          {formatPrice(order.total)}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-3" data-id="element-1410">
                        {order.status === 'paid' && order.lineItems.some(i => i.type === 'book') && <DownloadButton orderId={order.id} data-id="element-1411" />}
                        {order.trackingNumber && <span className="px-5 py-3 bg-[#F5F5F0] border border-[#D8D8D0] text-[#1F1F1F] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2" data-id="element-1412">
                            <Truck className="w-3.5 h-3.5" data-id="element-1413" />
                            {order.trackingCarrier ?? 'Tracking'} ·{' '}
                            {order.trackingNumber}
                          </span>}
                      </div>
                    </div>
                  </li>)}
              </ul>}
          </motion.div>
        </div>
      </section>
    </div>;
}